import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { reasons } from "../data/mock";
import { LOGO_TRANSPARENT } from "../data/mock";

const ReasonCard = ({ reason, index }) => {
  const Icon = reason.icon;
  const [tapped, setTapped] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
    >
      <div
        onClick={() => setTapped(!tapped)}
        className={`w-full text-left rounded-2xl border cursor-pointer md:cursor-default transition-all duration-400 group bg-white/60 border-transparent hover:bg-white hover:shadow-xl hover:shadow-[#16C79A]/8 hover:border-[#16C79A]/20 ${
          tapped ? "bg-white shadow-xl shadow-[#16C79A]/8 border-[#16C79A]/20" : ""
        }`}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            {/* Number + Icon */}
            <div
              className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 bg-[#0C2D48]/5 group-hover:bg-[#16C79A] group-hover:shadow-lg group-hover:shadow-[#16C79A]/25 ${
                tapped ? "bg-[#16C79A] shadow-lg shadow-[#16C79A]/25" : ""
              }`}
            >
              <Icon
                size={24}
                className={`transition-colors duration-300 text-[#0C2D48]/60 group-hover:text-white ${
                  tapped ? "text-white" : ""
                }`}
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs font-bold uppercase tracking-wider text-[#16C79A] group-hover:text-[#16C79A] transition-colors ${
                      tapped ? "text-[#16C79A]" : ""
                    }`}
                  >
                    Reason {reason.id}
                  </span>
                  <h3
                    className="text-lg font-bold transition-colors text-[#0C2D48]/80 group-hover:text-[#0C2D48]"
                  >
                    {reason.title}
                  </h3>
                </div>
                {/* Tap indicator - mobile only */}
                <ChevronDown
                  size={18}
                  className={`md:hidden flex-shrink-0 text-[#0C2D48]/30 transition-transform duration-300 ${
                    tapped ? "rotate-180 text-[#16C79A]" : ""
                  }`}
                />
              </div>

              {/* Mobile: tap hint */}
              {!tapped && (
                <span className="md:hidden text-xs text-[#16C79A]/60 mt-1 block">
                  Tap to read more
                </span>
              )}

              {/* Desktop: hover-reveal / Mobile: tap-reveal */}
              <div
                className={`overflow-hidden transition-all duration-400 max-h-0 opacity-0 group-hover:max-h-40 group-hover:opacity-100 group-hover:mt-3 ${
                  tapped ? "!max-h-40 !opacity-100 !mt-3" : ""
                }`}
              >
                <p className="text-slate-600 leading-relaxed text-[15px]">
                  {reason.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ReasonsSection = () => {
  return (
    <section id="reasons" className="py-24 lg:py-32 bg-[#f5fcf9] relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#16C79A]/10 to-transparent" />

      {/* Floating mascot - top right */}
      <motion.img
        src={LOGO_TRANSPARENT}
        alt=""
        aria-hidden="true"
        className="absolute -top-4 -right-12 w-48 opacity-[0.07] rotate-12 pointer-events-none select-none hidden lg:block"
        animate={{ y: [-8, 8, -8] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* Floating mascot - bottom left */}
      <motion.img
        src={LOGO_TRANSPARENT}
        alt=""
        aria-hidden="true"
        className="absolute -bottom-8 -left-10 w-40 opacity-[0.06] -rotate-12 pointer-events-none select-none hidden lg:block"
        animate={{ y: [6, -6, 6] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block text-[#16C79A] text-sm font-bold uppercase tracking-[0.2em] mb-4">
            The Facts
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0C2D48] mb-6">
            10 Amazing Benefits of Regular Bin{" "}
            <span className="text-[#16C79A]">Cleaning</span>
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Dubai Municipality mandates that we are responsible for cleaning,
            maintaining, and sanitizing waste containers to prevent pests and
            odors. Here's another 10 reasons why we should...
          </p>
        </motion.div>

        {/* Reasons Grid - 2 columns on desktop */}
        <div className="grid md:grid-cols-2 gap-4">
          {reasons.map((reason, index) => (
            <ReasonCard
              key={reason.id}
              reason={reason}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
